import { useMemo } from "react";
import { Bookmark } from "lucide-react";
import BookmarksOutlinedIcon from "@/assets/svg/BookmarksOutlined";
import CommonSelect from "./CommonSelect";

/**
 * Saved-search picker on the left of a search bar (Find accounts / Find contacts): pick a
 * saved search to run it, or "+ New Search" to start over.
 *
 * @param {{
 *   searches: Array<{ id: string | number; name?: string; query?: string }>;
 *   selectedId?: string | number | null;
 *   placeholder: string;
 *   onSelect: (search: any) => void;
 *   onNewSearch?: () => void;
 * }} props
 */
export default function SavedSearchSelect({ searches, selectedId, placeholder, onSelect, onNewSearch }) {
  const options = useMemo(
    () =>
      searches.map((s) => ({
        label: s.name || s.query || "Custom Search",
        value: String(s.id),
        startContent: (
          <Bookmark
            size={14}
            className={
              String(selectedId) === String(s.id) ? "text-secondary fill-secondary shrink-0" : "text-gray-400 shrink-0"
            }
          />
        ),
      })),
    [searches, selectedId],
  );

  return (
    <CommonSelect
      options={options}
      value={selectedId == null ? "" : String(selectedId)}
      placeholder={placeholder}
      ariaLabel="Saved searches"
      width="w-[212px]"
      startContent={<BookmarksOutlinedIcon size={16} color="#717680" />}
      onChange={(id) => {
        if (!id) return;
        const target = searches.find((s) => String(s.id) === String(id));
        if (target) onSelect(target);
      }}
      onCreateNew={onNewSearch}
      createNewLabel="+ New Search"
      classNames={{
        base: "!rounded-lg max-w-[212px]",
        trigger:
          "!border-0 !font-normal !text-sm !bg-transparent !shadow-none hover:!bg-transparent data-[hover=true]:!bg-transparent data-[pressed=true]:!bg-transparent hover:!shadow-none data-[hover=true]:!shadow-none h-8 px-1 data-[open=true]:!border-0 data-[open=true]:!ring-0 data-[focus=true]:!border-0 data-[focus=true]:!ring-0 focus-within:!border-0 focus-within:!ring-0 outline-none",
        innerWrapper: "!pr-7",
        value: "!text-sm !font-normal !text-gray-900 dark:!text-gray-100 !truncate !block",
        popoverContent: "min-w-[280px] max-w-[420px]",
      }}
    />
  );
}
